import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X, Plus, Trash2, CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const empty = {
  full_name: "",
  company: "",
  role_industry: "",
  phone: "",
  email: "",
  status_id: null,
  group_id: null,
  last_contact_date: "",
  next_follow_up_date: "",
  tags: [],
};

export default function ContactDialog({ open, onClose, contact, groups, statuses, onSaved }) {
  const isEdit = !!contact?.id;
  const [form, setForm] = useState(empty);
  const [tagInput, setTagInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [taskInput, setTaskInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(contact || null);

  useEffect(() => {
    if (open) {
      const base = contact || empty;
      setForm({
        full_name: base.full_name || "",
        company: base.company || "",
        role_industry: base.role_industry || "",
        phone: base.phone || "",
        email: base.email || "",
        status_id: base.status_id || null,
        group_id: base.group_id || null,
        last_contact_date: base.last_contact_date || "",
        next_follow_up_date: base.next_follow_up_date || "",
        tags: base.tags || [],
      });
      setLocal(contact || null);
      setTagInput("");
      setNoteInput("");
      setTaskInput("");
    }
  }, [open, contact]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.full_name.trim()) {
      toast.error("Full name is required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        last_contact_date: form.last_contact_date || null,
        next_follow_up_date: form.next_follow_up_date || null,
      };
      const r = isEdit
        ? await api.put(`/contacts/${contact.id}`, payload)
        : await api.post("/contacts", payload);
      toast.success(isEdit ? "Contact updated" : "Contact created");
      onSaved?.(r.data);
      setLocal(r.data);
      if (!isEdit) onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const addNote = async () => {
    if (!noteInput.trim() || !local?.id) return;
    const r = await api.post(`/contacts/${local.id}/notes`, { text: noteInput.trim() });
    setLocal({ ...local, notes: [...(local.notes || []), r.data] });
    setNoteInput("");
    onSaved?.();
  };

  const delNote = async (id) => {
    await api.delete(`/contacts/${local.id}/notes/${id}`);
    setLocal({ ...local, notes: (local.notes || []).filter((n) => n.id !== id) });
    onSaved?.();
  };

  const addTask = async () => {
    if (!taskInput.trim() || !local?.id) return;
    const r = await api.post(`/contacts/${local.id}/tasks`, { title: taskInput.trim(), completed: false });
    setLocal({ ...local, tasks: [...(local.tasks || []), r.data] });
    setTaskInput("");
    onSaved?.();
  };

  const toggleTask = async (t) => {
    const r = await api.put(`/contacts/${local.id}/tasks/${t.id}`, { completed: !t.completed });
    setLocal({ ...local, tasks: (local.tasks || []).map((x) => (x.id === t.id ? r.data : x)) });
    onSaved?.();
  };

  const delTask = async (id) => {
    await api.delete(`/contacts/${local.id}/tasks/${id}`);
    setLocal({ ...local, tasks: (local.tasks || []).filter((t) => t.id !== id) });
    onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="!max-w-2xl bg-white rounded-2xl border-slate-100 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="font-heading text-xl text-slate-900">
            {isEdit ? "Edit contact" : "New contact"}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[75vh] overflow-y-auto px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name" required>
              <input
                className="mr-input"
                value={form.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                data-testid="contact-fullname-input"
              />
            </Field>
            <Field label="Company">
              <input className="mr-input" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="contact-company-input" />
            </Field>
            <Field label="Role / Industry">
              <input className="mr-input" value={form.role_industry} onChange={(e) => set("role_industry", e.target.value)} />
            </Field>
            <Field label="Phone">
              <input className="mr-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
            <Field label="Email">
              <input className="mr-input" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </Field>
            <Field label="Group">
              <select
                className="mr-input"
                value={form.group_id || ""}
                onChange={(e) => set("group_id", e.target.value || null)}
                data-testid="contact-group-select"
              >
                <option value="">None</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                className="mr-input"
                value={form.status_id || ""}
                onChange={(e) => set("status_id", e.target.value || null)}
                data-testid="contact-status-select"
              >
                <option value="">None</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Last contact">
              <input type="date" className="mr-input" value={form.last_contact_date || ""} onChange={(e) => set("last_contact_date", e.target.value)} />
            </Field>
            <Field label="Next follow-up">
              <input type="date" className="mr-input" value={form.next_follow_up_date || ""} onChange={(e) => set("next_follow_up_date", e.target.value)} />
            </Field>
          </div>

          <Field label="Tags">
            <div className="flex flex-wrap items-center gap-1.5">
              {form.tags.map((t) => (
                <span key={t} className="mr-pill bg-slate-50 border-slate-200 text-slate-700">
                  {t}
                  <button onClick={() => set("tags", form.tags.filter((x) => x !== t))} className="text-slate-400 hover:text-slate-700">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                className="mr-input flex-1 min-w-[140px]"
                placeholder="Type and press Enter"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
            </div>
          </Field>

          {isEdit && local && (
            <>
              <div>
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 mb-2">Notes</div>
                <div className="space-y-2 mb-2">
                  {(local.notes || []).map((n) => (
                    <div key={n.id} className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="flex-1 text-sm text-slate-700 whitespace-pre-wrap">{n.text}</div>
                      <button onClick={() => delNote(n.id)} className="text-slate-400 hover:text-rose-500" data-testid={`delete-note-${n.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {(local.notes || []).length === 0 && <div className="text-sm text-slate-400">No notes yet.</div>}
                </div>
                <div className="flex gap-2">
                  <input
                    className="mr-input flex-1"
                    placeholder="Add a note…"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addNote()}
                    data-testid="add-note-input"
                  />
                  <button onClick={addNote} className="px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800" data-testid="add-note-button">
                    Add
                  </button>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 mb-2">Tasks / Action items</div>
                <div className="space-y-1.5 mb-2">
                  {(local.tasks || []).map((t) => (
                    <div key={t.id} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
                      <button onClick={() => toggleTask(t)} className="text-slate-500 hover:text-slate-900" data-testid={`toggle-task-${t.id}`}>
                        {t.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4" />}
                      </button>
                      <span className={`flex-1 text-sm ${t.completed ? "line-through text-slate-400" : "text-slate-700"}`}>{t.title}</span>
                      <button onClick={() => delTask(t.id)} className="text-slate-400 hover:text-rose-500" data-testid={`delete-task-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {(local.tasks || []).length === 0 && <div className="text-sm text-slate-400">No tasks yet.</div>}
                </div>
                <div className="flex gap-2">
                  <input
                    className="mr-input flex-1"
                    placeholder="Add a task…"
                    value={taskInput}
                    onChange={(e) => setTaskInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addTask()}
                    data-testid="add-task-input"
                  />
                  <button onClick={addTask} className="px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800" data-testid="add-task-button">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 font-heading">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60 font-heading"
            data-testid="contact-save-button"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create contact"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
